"use client"

import { useState } from 'react'
import Link from 'next/link'
import { STATE_SLUGS } from '@/lib/state-utils'
import { Button, buttonVariants } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { 
  MapPin, 
  Search, 
  DollarSign, 
  AlertCircle,
  Loader2,
  ChevronRight,
  Building2
} from "lucide-react"

interface UtilityResult {
  utility_slug: string
  utility_name: string
  state_code: string
  has_rebates: boolean
  rebate_count: number
  max_rebate_cents: number
}

interface LookupResult {
  zip_code: string
  state_code: string
  utilities: UtilityResult[]
  no_utilities: boolean
  error?: string
}

interface UtilityZipLookupProps {
  stateCode?: string
  stateName?: string
  className?: string
}

export function UtilityZipLookup({ stateCode, stateName, className = '' }: UtilityZipLookupProps) {
  const [zipInput, setZipInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [result, setResult] = useState<LookupResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  const handleLookup = async () => {
    // Validate ZIP code format
    const cleanZip = zipInput.trim()
    if (!/^\d{5}$/.test(cleanZip)) {
      setError('Please enter a valid 5-digit ZIP code')
      return
    }

    setIsLoading(true)
    setError(null)
    setResult(null)

    try {
      const response = await fetch(`/api/utility-lookup?zip=${cleanZip}`)
      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to lookup ZIP code')
      }

      // Filter by state if stateCode is provided
      if (stateCode && data.utilities) {
        data.utilities = data.utilities.filter(
          (u: UtilityResult) => u.state_code === stateCode
        )
        if (data.utilities.length === 0 && data.state_code !== stateCode) {
          setError(`ZIP code ${cleanZip} is not in ${stateName || stateCode}. Try a different ZIP code.`)
          setIsLoading(false)
          return
        }
      }

      setResult(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setIsLoading(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleLookup()
    }
  }

  return (
    <Card className={`bg-gradient-to-r from-[#0469f0]/5 to-[#004aad]/10 border-[#0469f0]/20 ${className}`}>
      <CardContent className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-[#0469f0]/10 p-2 rounded-full">
            <MapPin className="h-6 w-6 text-[#0469f0]" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Find Your Utility Rebates
            </h3>
            <p className="text-sm text-gray-600">
              Enter your ZIP code to see available rebates
              {stateName && ` in ${stateName}`}
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="flex-1">
            <Input
              type="text"
              name="zip"
              autoComplete="postal-code"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={5}
              value={zipInput}
              onChange={(e) => {
                // Only allow digits
                const value = e.target.value.replace(/\D/g, '')
                setZipInput(value)
                setError(null)
              }}
              onKeyDown={handleKeyDown}
              placeholder="Enter 5-digit ZIP code"
              className="w-full text-lg"
              aria-label="ZIP code"
            />
          </div>
          <Button variant="ctaSecondary" onClick={handleLookup} disabled={isLoading || zipInput.length !== 5} className="min-w-[140px]">
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Looking up...
              </>
            ) : (
              <>
                <Search className="h-4 w-4 mr-2" />
                Find Rebates
              </>
            )}
          </Button>
        </div>

        {/* Error State */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-700 font-medium">Unable to find utilities</p>
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* Results */}
        {result && (
          <div className="space-y-3">
            {result.no_utilities ? (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-yellow-800 font-medium">
                  No utility data found for ZIP {result.zip_code}
                </p>
                <p className="text-yellow-700 text-sm mt-1">
                  This ZIP code may be served by a municipal utility or cooperative 
                  not in our database. Contact your local utility directly.
                </p>
              </div>
            ) : result.utilities.length === 0 ? (
              <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <p className="text-gray-700 font-medium">
                  No refrigerator rebates available for ZIP {result.zip_code}
                </p>
                <p className="text-gray-600 text-sm mt-1">
                  The utilities serving this area don&apos;t currently offer 
                  refrigerator or freezer rebates. Check back later or explore 
                  our <Link href="/collections/energy-star-refrigerators" className="text-[#0469f0] hover:underline">Energy Star refrigerators</Link>.
                </p>
              </div>
            ) : (
              <>
                <p className="text-sm text-gray-600 mb-3">
                  Found {result.utilities.length} {result.utilities.length === 1 ? 'utility' : 'utilities'} serving ZIP {result.zip_code}:
                </p>
                
                {result.utilities.map((utility) => (
                  <div 
                    key={utility.utility_slug}
                    className="p-4 bg-white border border-gray-200 rounded-lg hover:border-[#0469f0]/50 hover:shadow-md transition-all"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="bg-gray-100 p-2 rounded-full">
                          <Building2 className="h-5 w-5 text-gray-600" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900">
                            {utility.utility_name}
                          </h4>
                          {utility.has_rebates ? (
                            <p className="text-sm text-green-600">
                              {utility.rebate_count} rebate{utility.rebate_count !== 1 ? 's' : ''} available
                            </p>
                          ) : (
                            <p className="text-sm text-gray-500">
                              No refrigerator rebates currently
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        {utility.has_rebates && (
                          <Badge className="bg-green-100 text-green-700">
                            <DollarSign className="h-3 w-3 mr-0.5" />
                            Up to {`$${utility.max_rebate_cents / 100}`}
                          </Badge>
                        )}
                        
                        {utility.has_rebates && (
                          <Link 
                            href={`/rebates/${STATE_SLUGS[utility.state_code]}/${utility.utility_slug}`}
                            className={buttonVariants({ variant: "outline", size: "sm" })}
                          >
                            View Rebates
                            <ChevronRight className="h-4 w-4 ml-1" />
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        {/* Hint text when no search yet */}
        {!result && !error && !isLoading && (
          <p className="text-xs text-gray-500 mt-2">
            We&apos;ll show which utilities serve your area and their available rebates.
          </p>
        )}
      </CardContent>
    </Card>
  )
}
